import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Plus, 
  MapPin, 
  Phone, 
  User,
  Percent,
  MoreVertical,
  Store,
  Building2,
  TrendingUp,
  Package,
  Pencil,
  Trash2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { locationsApi } from '@/api/locationsApi';
import { useToast } from '@/hooks/useToast';
import { AddLocationModal } from '@/components/locations/AddLocationModal';
import { EntityLink } from '@/components/detail/EntityLink';
import type { Location } from '@/types/api';

export function Locations() {
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const { success, error } = useToast();

  const { data: locations, isLoading } = useQuery({
    queryKey: ['locations'],
    queryFn: locationsApi.getAll,
  });

  const handleSuccess = () => {
    success(
      editingLocation ? 'Location Updated' : 'Location Created',
      editingLocation 
        ? 'Location has been updated successfully.' 
        : 'Your new location has been added to the system.',
      5000
    );
    setEditingLocation(null);
  };

  const handleEdit = (location: Location) => {
    setEditingLocation(location);
    setShowAddModal(true);
    setActiveMenu(null);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingLocation(null);
  };

  const handleDelete = async (location: Location) => {
    if (!confirm(`Are you sure you want to delete "${location.name}"?`)) {
      setActiveMenu(null);
      return;
    }
    
    try {
      await locationsApi.delete(location.id);
      success('Location Deleted', 'Location has been removed.', 5000);
    } catch (err) {
      error('Failed to Delete', 'Could not delete location. It may have inventory or sales associated with it.');
    }
    setActiveMenu(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-gray-900 dark:text-warm-cream">Locations</h1>
          <p className="text-gray-600 dark:text-warm-sand mt-1">
            Manage retail locations and consignment partners
          </p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="btn-primary"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Location
        </button>
      </div>

      {/* Locations Grid */}
      {isLoading ? (
        <div className="card p-12 text-center">
          <div className="flex items-center justify-center gap-3">
            <div className="w-5 h-5 border-2 border-amber-450/30 border-t-amber-450 rounded-full animate-spin" />
            <p className="text-gray-600 dark:text-warm-sand">Loading locations...</p>
          </div>
        </div>
      ) : locations?.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gray-200 dark:bg-charcoal-700 flex items-center justify-center">
              <Store className="w-8 h-8 text-gray-600 dark:text-warm-sand" />
            </div>
            <div>
              <p className="text-gray-900 dark:text-warm-cream font-medium text-lg">No locations yet</p>
              <p className="text-gray-600 dark:text-warm-sand text-sm mt-1">
                Add your first retail location or consignment partner
              </p>
            </div>
            <button 
              onClick={() => setShowAddModal(true)}
              className="btn-primary mt-2"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Location
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {locations?.map((location) => (
            <div key={location.id} className="card p-6 hover:border-blue-500/30 transition-all group">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/10 flex items-center justify-center border border-blue-500/20 group-hover:border-blue-500/40 transition-colors">
                    <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-gray-900 dark:text-warm-cream">
                      <EntityLink type="location" id={location.id} label={location.name} className="text-lg" />
                    </h3>
                    {location.isActive ? (
                      <span className="badge-success text-xs">Active</span>
                    ) : (
                      <span className="badge-danger text-xs">Inactive</span>
                    )}
                  </div>
                </div>
                <div className="relative">
                  <button 
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 rounded-lg transition-colors"
                    onClick={() => setActiveMenu(activeMenu === location.id ? null : location.id)}
                  >
                    <MoreVertical className="w-4 h-4 text-gray-600 dark:text-warm-sand" />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {activeMenu === location.id && (
                    <>
                      <div 
                        className="fixed inset-0 z-10"
                        onClick={() => setActiveMenu(null)}
                      />
                      <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-charcoal-800 border border-gray-200 dark:border-charcoal-700 rounded-lg shadow-lg z-20 py-1">
                        <button
                          onClick={() => handleEdit(location)}
                          className="w-full px-4 py-2 text-left text-sm text-gray-900 dark:text-warm-cream hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 flex items-center gap-2"
                        >
                          <Pencil className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(location)}
                          className="w-full px-4 py-2 text-left text-sm text-danger-400 hover:bg-danger-500/10 flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-3 text-sm">
                {location.address && (
                  <div className="flex items-start gap-3 text-gray-600 dark:text-warm-sand">
                    <MapPin className="w-4 h-4 mt-0.5 text-gray-400 dark:text-warm-taupe flex-shrink-0" />
                    <span className="line-clamp-2">{location.address}</span>
                  </div>
                )}
                
                {location.phone && (
                  <div className="flex items-center gap-3 text-gray-600 dark:text-warm-sand">
                    <Phone className="w-4 h-4 text-gray-400 dark:text-warm-taupe flex-shrink-0" />
                    <span>{location.phone}</span>
                  </div>
                )}
                
                {location.managerName && (
                  <div className="flex items-center gap-3 text-gray-600 dark:text-warm-sand">
                    <User className="w-4 h-4 text-gray-400 dark:text-warm-taupe flex-shrink-0" />
                    <span>Manager: {location.managerName}</span>
                  </div>
                )}

                <div className="flex items-center gap-3 pt-3 border-t border-gray-200 dark:border-charcoal-700">
                  <div className="w-8 h-8 rounded-lg bg-amber-450/10 flex items-center justify-center">
                    <Percent className="w-4 h-4 text-amber-450" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-warm-sand">Commission Rate</p>
                    <p className="font-medium text-gray-900 dark:text-warm-cream">{location.commissionRate}%</p>
                  </div>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-gray-200 dark:border-charcoal-700 flex gap-2">
                <button
                  type="button"
                  onClick={() => navigate(`/locations/${location.id}?tab=stock`)}
                  className="flex-1 px-4 py-2 bg-gray-100 dark:bg-charcoal-700 hover:bg-gray-200 dark:hover:bg-charcoal-600 text-gray-900 dark:text-warm-cream rounded-lg transition-colors text-sm font-medium"
                >
                  <Package className="w-4 h-4 mr-2 inline" />
                  Inventory
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/locations/${location.id}?tab=sales`)}
                  className="flex-1 px-4 py-2 bg-gray-100 dark:bg-charcoal-700 hover:bg-gray-200 dark:hover:bg-charcoal-600 text-gray-900 dark:text-warm-cream rounded-lg transition-colors text-sm font-medium"
                >
                  <TrendingUp className="w-4 h-4 mr-2 inline" />
                  Sales
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Location Modal */}
      <AddLocationModal 
        isOpen={showAddModal} 
        onClose={handleCloseModal} 
        onSuccess={handleSuccess}
        location={editingLocation}
      />
    </div>
  );
}
